import { useState, type JSX } from 'react';

/**
 * N5.8 / R8 (round-6) — Autonomy Ladder slider.
 *
 * Renders a 5-rung control so an operator can set an agent's (or a
 * phase's) autonomy level. The rungs come from the
 * `crates/shakti-auth/src/rbac.rs::AutonomyRung` enum:
 *
 *   1. ReadOnly          — observes; never writes
 *   2. SandboxedWrite    — writes in the tenant sandbox only
 *   3. ReviewedWrite     — writes to shared state but every write
 *                          needs a human approval
 *   4. BatchedReview     — reviews aggregated per phase/day
 *   5. Autonomous        — trusted to execute without per-write review
 *
 * Changes persist via `POST /v1/agents/{id}/autonomy`; the response
 * becomes the controlled value for the next render.
 */
export interface AutonomyLadderSliderProps {
  agentId: string;
  initialRung: 1 | 2 | 3 | 4 | 5;
  onPersist: (rung: 1 | 2 | 3 | 4 | 5) => Promise<void>;
  disabled?: boolean;
}

const LABELS: Record<1 | 2 | 3 | 4 | 5, { name: string; hint: string }> = {
  1: { name: 'Read only', hint: 'Observes; never writes' },
  2: { name: 'Sandboxed', hint: 'Writes in the tenant sandbox only' },
  3: { name: 'Reviewed', hint: 'Every write needs human approval' },
  4: { name: 'Batched', hint: 'Reviews aggregated per phase / day' },
  5: { name: 'Autonomous', hint: 'Executes without per-write review' },
};

export function AutonomyLadderSlider({
  agentId,
  initialRung,
  onPersist,
  disabled,
}: AutonomyLadderSliderProps): JSX.Element {
  const [rung, setRung] = useState<1 | 2 | 3 | 4 | 5>(initialRung);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleChange(next: 1 | 2 | 3 | 4 | 5): Promise<void> {
    const prev = rung;
    setRung(next);
    setSaving(true);
    setError(null);
    try {
      await onPersist(next);
    } catch (e) {
      setRung(prev);
      setError(e instanceof Error ? e.message : 'failed to persist rung');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="flex flex-col gap-2"
      role="group"
      aria-labelledby={`autonomy-${agentId}-label`}
    >
      <div className="flex items-center justify-between">
        <span
          id={`autonomy-${agentId}-label`}
          className="text-sm font-medium text-fg-primary"
        >
          Autonomy
        </span>
        <span className="shakti-mono text-2xs text-fg-muted">
          {saving ? 'saving…' : `rung ${rung} · ${LABELS[rung].name}`}
        </span>
      </div>
      <input
        type="range"
        min={1}
        max={5}
        step={1}
        value={rung}
        disabled={disabled || saving}
        onChange={(e) => void handleChange(Number(e.target.value) as 1 | 2 | 3 | 4 | 5)}
        aria-valuemin={1}
        aria-valuemax={5}
        aria-valuenow={rung}
        aria-valuetext={LABELS[rung].name}
      />
      <div className="flex justify-between text-2xs text-fg-muted">
        {([1, 2, 3, 4, 5] as const).map((r) => (
          <span
            key={r}
            className={r === rung ? 'text-fg-primary font-semibold' : undefined}
          >
            {r}
          </span>
        ))}
      </div>
      <p className="text-xs text-fg-secondary">{LABELS[rung].hint}</p>
      {error && <p className="text-xs text-status-error">{error}</p>}
    </div>
  );
}
